import CampaignService, { CampaignTableType } from '../services/CampaignService';
import { Campaign, CampaignStatus, URLShortner } from '../models';
import UtilsService from '../services/UtilsService';
import ShortLinkService from '../services/ShortLinkService';

export interface CampaignState {
  campaigns: Campaign[];
  count: number;
  counters: {
    [CampaignTableType.ALL]: 0,
    [CampaignTableType.RUNNING]: 0,
    [CampaignTableType.DRAFT]: 0,
    [CampaignTableType.PAUSED]: 0,
    [CampaignTableType.FINISHED]: 0,
  };
  campaign: Campaign;
  campaignShortLinks: URLShortner[];
  loading: boolean;
}

export const CampaignModule: {
  state: CampaignState,
  mutations: any,
  actions: any,
} = {
  state: {
    campaigns: [],
    count: 0,
    counters: {
      [CampaignTableType.ALL]: 0,
      [CampaignTableType.RUNNING]: 0,
      [CampaignTableType.DRAFT]: 0,
      [CampaignTableType.PAUSED]: 0,
      [CampaignTableType.FINISHED]: 0,
    },
    campaign: null as any,
    campaignShortLinks: [],
    loading: false,
  },
  mutations: {
    updateCampaign(state: CampaignState, campaign: Campaign) {
      const index = state.campaigns.findIndex((c) => c.objectId === campaign.objectId);
      if (index !== -1) {
        state.campaigns[index] = {...state.campaigns[index], ...campaign};
      }
    },
    addCampaign(state: CampaignState, campaign: Campaign) {
      state.campaigns.push(campaign);
    },
    setCampaigns(state: CampaignState, campaigns: Campaign[]) {
      state.campaigns = campaigns;
    },
    setCampaign(state: CampaignState, campaign: Campaign) {
      state.campaign = campaign;
    },
    setCampaignsCount(state: CampaignState, count: number) {
      state.count = count;
    },
    deleteCampaign(state: CampaignState, campaignId: string) {
      const newCampaigns = state.campaigns.filter((c) => c.objectId !== campaignId);
      state.campaigns = newCampaigns;
    },
    setCampaignLoading(state: CampaignState, loading: boolean) {
      state.loading = loading;
    },
    setCampaignShortLinks(state: CampaignState, shortLinks: URLShortner[]) {
      state.campaignShortLinks = shortLinks;
    },
    addShortLink(state: CampaignState, shortLink: URLShortner) {
      state.campaignShortLinks = [...state.campaignShortLinks, shortLink];
    },
    setCounters(state: CampaignState, counters: any) {
      state.counters = counters;
    },
  },
  actions: {
    async refreshShortLinks({commit}: any, campaignId: string) {
      const shortLinks = await ShortLinkService.getAllShortLink(campaignId);
      commit('setCampaignShortLinks', shortLinks);
    },
    async loadCampaignsByType(
      {commit}: any,
      payload = {
        page: 1, pageSize: 10, orderBy: '', orderDesc: false, type: CampaignTableType,
      },
    ) {
      commit('setCampaignLoading', true);
      commit('setCampaigns', []);
      const result = await CampaignService.getAllCampaignsByType(
        payload.page, payload.pageSize, payload.orderBy, payload.orderDesc, payload.type as any,
      ).catch((error) => {
        commit('setCampaignLoading', false);
        throw error;
      });
      commit('setCampaignLoading', false);
      commit('setCampaignsCount', result.count);
      commit('setCampaigns', result.results);
    },
    createCampaignDraft({commit}: any) {
      return CampaignService.createCampaignDraft().then((res) => {
        commit('addCampaign', res);
        commit('setCampaign', res);
        return res;
      });
    },
    async createCampaign({commit}: any, payload: any) {
      const campaign = await CampaignService.createCampaign(payload);
      commit('addCampaign', campaign);
    },
    async deleteCampaign({commit}: any, campaignId: any) {
      await CampaignService.deleteCampaign(campaignId);
      commit('deleteCampaign', campaignId);
    },
    async updateCampaign({commit}: any, campaign: Campaign) {
      await CampaignService.updateCampaign(campaign.objectId as string, campaign);
      if (campaign.status === CampaignStatus.Cancelled) {
        commit('deleteCampaign', campaign.objectId);
      } else {
        commit('updateCampaign', campaign);
      }
    },
    async uploadCampaignAudience({commit}: any, file: File) {
      const newFile = new File([file], UtilsService.stripFilename(file), {type: file.type});
      const audienceFile = await CampaignService.uploadCampaignAudience(newFile);
      return audienceFile;
    },
    async loadCapaignById({commit}: any, campaignId: string) {
      return CampaignService.loadCampaignById(campaignId).then(async (res) => {
        const shortLinks = await ShortLinkService.getAllShortLink(res.objectId);
        commit('setCampaign', res);
        commit('setCampaignShortLinks', shortLinks);
        return res;
      });
    },
    async loadCampaignCounters({commit}: any) {
      return CampaignService.loadCampaignCounters().then((res) => {
        commit('setCounters', res);
        return res;
      });
    },
  },
};
